import React, { useCallback, useEffect, useRef } from 'react';
import TableHeader from '../../BaseComponents/TableHeader';
import './ClinicsListTable.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  appendClinicsList,
  ClinicsPageState
} from '../../../store/clinicsSlice';
import ClinicsTableRow from './ClinicsTableRow';
import { getClinicsList } from '../../../api/clinics';

const headerColumns = [
  // { title: 'Avatar', key: 'avatar', minWidth: '64px', flex: 0 },
  { title: 'Clinic', key: 'clinic', minWidth: '180px', flex: 0 },
  { title: 'Address', key: 'address', minWidth: '160px', flex: 0 },
  { title: 'Bio', key: 'bio', minWidth: '252px', flex: 1 },
  { title: 'Contacts', key: 'contacts', minWidth: '270px', flex: 0 },
  { title: 'Description', key: 'description', minWidth: '252px', flex: 0 },
  { title: '', key: 'actions', minWidth: '48px', flex: 0 },
];

const ClinicsTable: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const dispatch = useDispatch();
  const { clinicsList, limit } = useSelector(
    (state: { clinics: ClinicsPageState }) => state.clinics
  );

  const offsetRef = useRef<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadMoreClinics = useCallback(async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    offsetRef.current += limit;
    try {
      const clinicsList = await getClinicsList(
        offsetRef.current,
        limit
      );
      if (clinicsList.clinics.length > 0) {
        dispatch(appendClinicsList(clinicsList.clinics));
        setHasMore(clinicsList.clinics.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('fetchClinics error :>> ', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, limit, loading, hasMore]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || loading || !hasMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 100;
      
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreClinics();
      }
    }, 150);
  }, [loading, hasMore, loadMoreClinics]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const clinics = clinicsList;

  return (
    <div className='scroll-container'>
      <div className='table-container clinics-table'>
        <TableHeader headerColumns={headerColumns} gap={'24px'} />
        <div className='table-body' ref={scrollContainerRef}>
          {clinics &&
            clinics.map((clinic) => {
              return <ClinicsTableRow clinicData={clinic} key={clinic.id} />;
            })}
        </div>
      </div>
    </div>
  );
};

export default ClinicsTable;
